import React, { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { SendOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';

import MyCollapse from 'components/Collapse';
import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import MyModal from 'components/MyModal';
import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import { HOST_ORBITA } from 'settings';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import affiliationsService from 'v2/services/affiliations';
import { useUserStore } from 'v2/store/User';

import type { IRecruitment } from './interfaces';
import RecruitmentSchema from './validations';

interface IProps {
  open: boolean;
  closeModal: (...args: any[]) => any;
}

const InitialValues = {
  email: ''
};

const RecruitmentCommission: React.FC<IProps> = ({ open, closeModal }) => {
  const [isLoading] = useObservable(() => affiliationsService.isLoadingRecruitment(), []);
  const [product] = useObservable(() => affiliationsService.getProductRecruitment(), []);

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  const onSubmit = useCallback(
    (values: IRecruitment, { setSubmitting, resetForm }: FormikProps<IRecruitment>) => {
      setSubmitting(true);

      affiliationsService.inviteAffiliationByEmail(product, values.email).subscribe(
        () => {
          toast.success('Convite enviado com sucesso.');
          setSubmitting(false);
          resetForm();
        },
        err => {
          toast.error(err);
          setSubmitting(false);
        }
      );
    },
    [product]
  );

  if (!open) {
    return null;
  }

  return (
    <MyModal
      title='Comissão por recrutamento'
      hasActive
      hideButtonOk
      hasButtons
      textButtonCancel='Fechar'
      loading={isLoading}
      closeModal={() => closeModal()}
    >
      <div className='w-full'>
        {(isLoading === null || isLoading) && <Spin className='flex justify-center' />}

        {!isLoading && !product && (
          <span className='block text-sm'>Algo deu errado, tente novamente mais tarde...</span>
        )}

        {!isLoading && product && (
          <>
            <div className='block w-full text-[15px] font-normal'>
              Este conteúdo paga comissão por Indicações de Afiliados:
              <br />
              {product.recruit_commission_type && product.recruit_commission_type === 'P' && (
                <>
                  Comissão: <b>{product.recruit_commission_value}%</b>
                  {product.recruit_max_sales ? ` nas ${product.recruit_max_sales} primeiras vendas.` : null}
                </>
              )}
              {product.recruit_commission_type && product.recruit_commission_type !== 'P' && (
                <>
                  Comissão: <b>R${product.recruit_commission_value}</b>
                  {product.recruit_max_sales ? ` nas ${product.recruit_max_sales} primeiras vendas.` : null}
                </>
              )}
            </div>

            <div className='m-4'></div>

            <MyCollapse title='Convite por link' arrow contentActive>
              <CustomCopyToClipboard
                disableUnderline
                text={`${HOST_ORBITA}/affiliate/invite/${product.id}/${currentUserId}`}
                fullWidth
              />

              <div className='m-2'></div>

              <span className='block text-xs'>
                Divulgue esse link para que se cadastrem automaticamente atrelados ao seu conteúdo.
              </span>
            </MyCollapse>

            <div className='m-2'></div>

            <MyCollapse title='Convidar por e-mail' arrow>
              <Formik initialValues={InitialValues} validationSchema={RecruitmentSchema} onSubmit={onSubmit}>
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form>
                    <div className='flex items-center gap-2'>
                      <div className='flex-1'>
                        <Field
                          id='email'
                          name='email'
                          fullWidth
                          disabled={isSubmitting}
                          component={TextFormField}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('email', e.target.value)}
                          suffix={
                            <Button
                              type='text'
                              size='small'
                              htmlType='submit'
                              icon={<SendOutlined style={{ fontSize: 16 }} />}
                              loading={isSubmitting}
                              disabled={isSubmitting || !values.email}
                            />
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>

              <span className='block text-xs'>O afiliado precisa estar cadastrado na Eduzz.</span>
            </MyCollapse>
          </>
        )}
      </div>
    </MyModal>
  );
};

export default memo(RecruitmentCommission);
